"use client"

import { useState } from "react"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import * as Sentry from "@sentry/nextjs"
import { Button } from "@/components/ui/button"
import { track } from "@/lib/analytics"
import type { Answers, Recommendation } from "@/lib/quiz/types"
import type { PriceMap } from "@/lib/pricing"
import {
  INSURANCE_CHECKER_URL,
  buildProformaRequestBody,
} from "@/lib/quiz/proforma"

interface Props {
  answers: Answers
  recommendation: Recommendation
  totalPriceChf: number
  prices: PriceMap
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

type Status = "idle" | "submitting" | "success" | "error"

export function ProFormaForm({
  answers,
  recommendation,
  totalPriceChf,
  prices,
}: Props) {
  const t = useTranslations("quiz.proforma")
  const locale = useLocale()
  const [name, setName] = useState("")
  const [email, setEmail] = useState(answers.email ?? "")
  const [status, setStatus] = useState<Status>("idle")
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)

  const handleSubmit = async () => {
    if (!name || !EMAIL_RE.test(email)) return
    setStatus("submitting")
    track.proFormaRequested({
      recommendation_base: recommendation.baseTier,
      recommendation_addons: recommendation.addOns,
      // Omit rather than emit NaN — a missing feed price makes totalPrice() NaN,
      // which serializes to null and pollutes the event (EAGNT-233).
      total_price_chf: Number.isFinite(totalPriceChf)
        ? totalPriceChf
        : undefined,
    })

    const body = buildProformaRequestBody({
      email,
      name,
      locale,
      baseTier: recommendation.baseTier,
      addOns: recommendation.addOns,
      gender: answers.gender,
      insurerId: answers.insurer_id,
      insurerPackageId: answers.insurer_package_id,
      prices,
    })

    try {
      // Direct call to the CORS-open insurance-checker endpoint. In auto mode
      // it returns the generated PDF inline for instant download; otherwise it
      // queues the proforma (200/201/202) and the team / email-flows handle it.
      const res = await fetch(`${INSURANCE_CHECKER_URL}/api/proforma-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      })

      const contentType = res.headers.get("content-type") ?? ""
      if (res.status === 200 && contentType.includes("application/pdf")) {
        const blob = await res.blob()
        setPdfUrl(URL.createObjectURL(blob))
        setStatus("success")
      } else if (res.ok || res.status === 202) {
        // Queued / async fallback — no PDF, but the request is accepted.
        setStatus("success")
      } else {
        setStatus("error")
      }
    } catch (err) {
      Sentry.captureException(err, { tags: { feature: "quiz-proforma" } })
      setStatus("error")
    }
  }

  if (status === "success") {
    return (
      <div>
        <h3 className="type-headline-md text-deep-blue-900">
          {t("successTitle")}
        </h3>
        {pdfUrl ? (
          <a
            href={pdfUrl}
            download="pro-forma-invoice.pdf"
            className="bg-deep-blue-900 hover:bg-deep-blue-700 type-title-md mt-4 inline-flex items-center rounded-full px-6 py-3 text-white transition-colors"
          >
            {t("downloadCta")}
          </a>
        ) : (
          <p className="type-body-lg mt-2 text-neutral-700">
            {t("successBody")}
          </p>
        )}
      </div>
    )
  }

  return (
    <div id="pro-forma">
      <h3 className="type-headline-md text-deep-blue-900">{t("title")}</h3>
      <p className="type-body-lg mt-2 text-neutral-700">{t("subtitle")}</p>

      <div className="mt-6 space-y-4">
        <div>
          <label className="type-body-sm mb-1 block text-neutral-700">
            {t("nameLabel")}
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("namePlaceholder")}
            className="focus:border-deep-blue-900 type-body-lg w-full rounded-[12px] border border-neutral-300 bg-white px-4 py-3 focus:outline-none"
          />
        </div>
        <div>
          <label className="type-body-sm mb-1 block text-neutral-700">
            {t("emailLabel")}
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="focus:border-deep-blue-900 type-body-lg w-full rounded-[12px] border border-neutral-300 bg-white px-4 py-3 focus:outline-none"
          />
        </div>
        {status === "error" && (
          <p className="type-body-sm text-red-600">{t("error")}</p>
        )}
        <Button
          onClick={handleSubmit}
          disabled={status === "submitting" || !name || !email}
        >
          {status === "submitting" ? t("submitting") : t("submitCta")}
        </Button>
      </div>
    </div>
  )
}
